import React, { useState } from 'react';
import { Asset } from '../../../types/asset';
import styled from 'styled-components';
import { Document } from '@contentful/rich-text-types';
import { Testimonial } from '../testimonial';
import { RichText } from '../../richText';

import SwiperCore, { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';

interface Testimonial {
    name: string;
    position: string;
    company: string;
    image?: Asset;
    content: Document;
    backgroundColour?: string;
}

interface TestimonialProps {
    heading: string;
    layout: string;
    ctaLabel?: string;
    ctaLink?: string;
    testimonials: Testimonial[];
}

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr;

    @media (min-width: 1079px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const TestimonialItem = (props: Testimonial) => {
    return(
        <div className="flex flex-col items-start items-center max-w-4xl px-12 mx-auto md:px-4 md:flex-row md:items-start">
            <div className="flex-shrink-0 hidden w-24 h-24 mt-1 mb-10 mr-12 bg-white rounded-full md:block ">
                {/* tslint:disable-next-line:max-line-length */}
                <img className="rounded-full" src={props.image && props.image.url} alt={props.image && props.image.name} />
            </div>
            <div className="w-auto">
                <div className="mb-5 text-left">
                    <RichText content={props.content} />
                </div>
                <div className="text-sm text-left">
                    <span className="font-semibold">{props.name}</span>
                    <span> {props.position} - </span>
                    <span>{props.company}</span>
                </div>
            </div>
        </div>
    );
};

const CTA = styled.a`
    &::after {
        content: "";
        position: absolute;
        bottom: -2px;
        left: 0;
        right: 0;
        height: 3px;
        background-color: #46ff46;
        transition: all .3s ease-in-out 0s;
        transform: scaleX(1);
    }
`;

SwiperCore.use([Navigation]);

export const TestimonialCollection: React.FC<TestimonialProps> = ({ heading, ctaLabel, ctaLink, layout, testimonials}) => {
    if (layout === 'grid') {
        return (
            <div className="w-full border-b border-stone">
                <div className="w-full px-0 mx-auto text-center">
                    <GridContainer>
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className={`${index < testimonials.length - 2 ? 'border-b' : ''} border-l border-stone ${index % 2 !== 0 ? 'border-r' : ''}`}>
                                <div className="py-20">
                                    <TestimonialItem {...testimonial} />
                                </div>
                            </div>
                        ))}
                    </GridContainer>
                </div>
                {ctaLabel &&
                    (
                        <div className="px-6 pt-4 pb-5">
                            <CTA className="relative" href={`${ctaLink}`}>
                                <span className="py-2 mb-2 text-lg uppercase">{ctaLabel}</span>
                            </CTA>
                        </div>
                    )
                }
            </div>
        );
    }
    return (
        <div className="w-full py-20 border-b border-stone">
            <div className="w-full px-6 mx-auto text-center md:w-10/12 md:px-0">
                <div className="uppercase mb-16 text-1.5xl">{heading}</div>
                <div>
                    <Swiper
                        navigation={true}
                        spaceBetween={50}
                        slidesPerView={1}
                        grabCursor={true}
                        centeredSlides={true}
                        loop={true}
                    >
                    {testimonials.map((testimonial, index) => (
                        <SwiperSlide key={index}>
                            <div className="flex flex-col justify-center h-full">
                                <div>
                                    <TestimonialItem {...testimonial} />
                                </div>
                            </div>
                        </SwiperSlide>
                    ))}
                    </Swiper>
                </div>

                {ctaLabel &&
                    (
                        <div className="px-6 pt-12">
                            <CTA className="relative" href={`${ctaLink}`}>
                                <span className="py-2 mb-2 text-lg uppercase">{ctaLabel}</span>
                            </CTA>
                        </div>
                    )
                }
            </div>
        </div>
    );
};
